import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import MenuItem from '../../../../element/ui/core/menu-item';
import {
  TEXT_FONT_GROUPS,
  ensureGoogleTextFontsLoaded,
  getFontLabel,
  normalizeFontOption,
  resolveFontFamilyForCanvas,
} from '../../../../../../textFonts.config';
import SidebarPanelButton from '../../../../components/common/SidebarPanelButton';
import { StyledFontFamilySelect } from './TextOptions.styled';

const FONT_MENU_SEARCH_SELECTOR =
  '.respark-font-family-menu .respark-font-family-search-input';
const FONT_MENU_ROOT_SELECTOR = '.respark-font-family-menu';
const FOCUS_SEARCH_MAX_ATTEMPTS = 12;

const buildFlatFontOptions = (filteredGroups) => {
  const options = [];
  filteredGroups.forEach((group) => {
    group.fonts.forEach((font) => {
      const { label, value: fontValue } = normalizeFontOption(font);
      options.push({ label, value: fontValue, groupId: group.id });
    });
  });
  return options;
};

const FontFamilyPicker = ({
  value,
  onChange,
  placeholder,
  disabled = false,
  isSelectionActive = true,
}) => {
  const anchorRef = useRef(null);
  const searchInputRef = useRef(null);
  const skipMenuScrollRef = useRef(false);
  const [menuWidth, setMenuWidth] = useState(null);
  const [search, setSearch] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [selectInstanceKey, setSelectInstanceKey] = useState(0);

  const filteredGroups = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return TEXT_FONT_GROUPS;
    }

    return TEXT_FONT_GROUPS.map((group) => ({
      ...group,
      fonts: group.fonts.filter((font) => {
        const { label, value: fontValue } = normalizeFontOption(font);
        return (
          label.toLowerCase().includes(query) ||
          fontValue.toLowerCase().includes(query)
        );
      }),
    })).filter((group) => group.fonts.length > 0);
  }, [search]);

  const fontOptions = useMemo(
    () => buildFlatFontOptions(filteredGroups),
    [filteredGroups],
  );

  const isFontMenuOpen = useCallback(
    () => Boolean(document.querySelector(FONT_MENU_ROOT_SELECTOR)),
    [],
  );

  const closeFontMenu = useCallback(() => {
    if (!isFontMenuOpen()) {
      return;
    }
    setSearch('');
    setHighlightedIndex(-1);
    setSelectInstanceKey((key) => key + 1);
  }, [isFontMenuOpen]);

  const openFontMenu = useCallback(() => {
    if (disabled || isFontMenuOpen()) {
      return;
    }
    const trigger = anchorRef.current?.querySelector('.respark_Select-root');
    trigger?.click();
  }, [disabled, isFontMenuOpen]);

  const syncHighlightToValue = useCallback(() => {
    if (fontOptions.length === 0) {
      setHighlightedIndex(-1);
      return;
    }
    skipMenuScrollRef.current = true;
    const currentIndex = fontOptions.findIndex((option) => option.value === value);
    setHighlightedIndex(currentIndex >= 0 ? currentIndex : 0);
  }, [fontOptions, value]);

  const focusSearchInput = useCallback(() => {
    if (disabled) {
      return;
    }

    const tryFocus = (attempt) => {
      const input =
        searchInputRef.current ||
        document.querySelector(FONT_MENU_SEARCH_SELECTOR);

      if (input instanceof HTMLInputElement) {
        input.focus({ preventScroll: true });
        return;
      }

      if (attempt < FOCUS_SEARCH_MAX_ATTEMPTS) {
        requestAnimationFrame(() => tryFocus(attempt + 1));
      }
    };

    tryFocus(0);
  }, [disabled]);

  const handleFontMenuContainerRef = useCallback(
    (node) => {
      if (node) {
        ensureGoogleTextFontsLoaded();
        syncHighlightToValue();
        requestAnimationFrame(() => focusSearchInput());
      }
    },
    [focusSearchInput, syncHighlightToValue],
  );

  const handleSearchInputRef = useCallback((node) => {
    searchInputRef.current = node;
  }, []);

  const handleFontMenuClose = useCallback(() => {
    setHighlightedIndex(-1);
  }, []);

  const selectHighlightedFont = useCallback(() => {
    const option = fontOptions[highlightedIndex];
    if (!option) {
      return;
    }
    setSearch('');
    onChange(option.value);
    closeFontMenu();
  }, [closeFontMenu, fontOptions, highlightedIndex, onChange]);

  const handleSearchKeyDown = useCallback(
    (event) => {
      event.stopPropagation();

      if (fontOptions.length === 0) {
        if (event.key === 'Escape') {
          event.preventDefault();
          closeFontMenu();
        }
        return;
      }

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setHighlightedIndex((prev) => {
            if (prev < 0) {
              return 0;
            }
            return Math.min(prev + 1, fontOptions.length - 1);
          });
          break;
        case 'ArrowUp':
          event.preventDefault();
          setHighlightedIndex((prev) => {
            if (prev <= 0) {
              return -1;
            }
            return prev - 1;
          });
          break;
        case 'Enter':
          if (highlightedIndex >= 0) {
            event.preventDefault();
            selectHighlightedFont();
          }
          break;
        case 'Escape':
          event.preventDefault();
          closeFontMenu();
          break;
        default:
          break;
      }
    },
    [
      closeFontMenu,
      fontOptions.length,
      highlightedIndex,
      selectHighlightedFont,
    ],
  );

  const handleTriggerKeyDown = useCallback(
    (event) => {
      if (disabled) {
        return;
      }

      if (
        event.key === 'ArrowDown' ||
        event.key === 'Enter' ||
        event.key === ' '
      ) {
        event.preventDefault();
        openFontMenu();
      }
    },
    [disabled, openFontMenu],
  );

  const syncMenuWidth = useCallback(() => {
    const el = anchorRef.current;
    if (!el) {
      return;
    }
    const width = Math.round(el.getBoundingClientRect().width);
    if (width > 0) {
      setMenuWidth((prev) => (prev === width ? prev : width));
    }
  }, []);

  useEffect(() => {
    const el = anchorRef.current;
    if (!el) {
      return undefined;
    }
    syncMenuWidth();
    const observer = new ResizeObserver(syncMenuWidth);
    observer.observe(el);
    window.addEventListener('resize', syncMenuWidth);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', syncMenuWidth);
    };
  }, [syncMenuWidth]);

  useEffect(() => {
    if (!isSelectionActive) {
      closeFontMenu();
    }
  }, [closeFontMenu, isSelectionActive]);

  useEffect(
    () => () => {
      if (document.querySelector(FONT_MENU_ROOT_SELECTOR)) {
        document.body.classList.remove('Menu-open');
      }
    },
    [],
  );

  useEffect(() => {
    if (highlightedIndex < 0) {
      return;
    }
    if (skipMenuScrollRef.current) {
      skipMenuScrollRef.current = false;
      return;
    }
    const menu = document.querySelector(FONT_MENU_ROOT_SELECTOR);
    const row = menu?.querySelector(
      `[data-font-option-index="${highlightedIndex}"]`,
    );
    row?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [highlightedIndex]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (!isFontMenuOpen()) {
        return;
      }
      const anchor = anchorRef.current;
      const menu = document.querySelector(FONT_MENU_ROOT_SELECTOR);
      if (
        anchor?.contains(event.target) ||
        menu?.contains(event.target)
      ) {
        return;
      }
      closeFontMenu();
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
    };
  }, [closeFontMenu, isFontMenuOpen]);

  useEffect(() => {
    setHighlightedIndex((prev) => {
      if (fontOptions.length === 0) {
        return -1;
      }
      if (prev < 0) {
        return prev;
      }
      if (prev >= fontOptions.length) {
        return fontOptions.length - 1;
      }
      return prev;
    });
  }, [fontOptions]);

  const handleFontChange = (newFontFamily) => {
    setSearch('');
    setHighlightedIndex(-1);
    onChange(newFontFamily);
  };

  const menuChildren = useMemo(() => {
    const items = [
      <div
        key="font-search"
        className="respark-font-family-search"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <input
          ref={handleSearchInputRef}
          type="text"
          className="respark-font-family-search-input"
          autoComplete="off"
          spellCheck={false}
          placeholder="Search fonts…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={handleSearchKeyDown}
          aria-label="Search fonts"
          aria-activedescendant={
            highlightedIndex >= 0
              ? `respark-font-option-${highlightedIndex}`
              : undefined
          }
          role="combobox"
          aria-expanded={isFontMenuOpen()}
          aria-controls="respark-font-family-listbox"
        />
      </div>,
    ];

    let optionIndex = 0;
    filteredGroups.forEach((group) => {
      items.push(
        <div
          key={`group-${group.id}`}
          className="respark-font-family-group-label"
          aria-hidden
        >
          {group.label}
        </div>,
      );

      group.fonts.forEach((font) => {
        const { label, value: fontValue } = normalizeFontOption(font);
        const index = optionIndex;
        optionIndex += 1;
        const previewFontFamily = resolveFontFamilyForCanvas(fontValue);
        const isKeyboardActive = index === highlightedIndex;

        items.push(
          <MenuItem
            className={`respark_text-font-family-item${
              isKeyboardActive ? ' respark_font-family-item--keyboard-active' : ''
            }`}
            key={fontValue}
            value={fontValue}
            style={{ fontFamily: previewFontFamily }}
            id={`respark-font-option-${index}`}
            data-font-option-index={index}
            onMouseEnter={() => setHighlightedIndex(index)}
          >
            <span
              className="respark-font-family-preview-label"
              style={{ fontFamily: previewFontFamily }}
            >
              {label}
            </span>
          </MenuItem>,
        );
      });
    });

    if (filteredGroups.length === 0) {
      items.push(
        <div key="font-empty" className="respark-font-family-empty">
          No fonts match your search
        </div>,
      );
    }

    return items;
  }, [
    filteredGroups,
    handleSearchInputRef,
    handleSearchKeyDown,
    highlightedIndex,
    isFontMenuOpen,
    search,
  ]);

  const menuContainerStyle =
    menuWidth != null
      ? { minWidth: menuWidth, width: menuWidth, maxWidth: menuWidth }
      : undefined;

  return (
    <div
      ref={anchorRef}
      className="respark_text-font-family-anchor"
      onMouseDown={syncMenuWidth}
    >
      <SidebarPanelButton
        className="respark_text-font-family-wrap"
        Icon={null}
        title={placeholder}
        disabled={disabled}
      >
        <StyledFontFamilySelect
          key={selectInstanceKey}
          className="respark_text-font-family-option"
          fullWidth
          disabled={disabled}
          onChange={handleFontChange}
          onKeyDown={handleTriggerKeyDown}
          value={value}
          placeholder={placeholder}
          renderLabel={(selectedValue) => (
            <span
              className="respark-font-family-trigger-label"
              style={{
                fontFamily: resolveFontFamilyForCanvas(selectedValue),
                fontSize: 12,
                lineHeight: 1.2,
              }}
              title={getFontLabel(selectedValue)}
            >
              {getFontLabel(selectedValue)}
            </span>
          )}
          size="sm"
          MenuProps={{
            onClose: handleFontMenuClose,
            hideScroll: false,
            enableOverlay: false,
            containerProps: {
              className: 'respark-font-family-menu',
              id: 'respark-font-family-listbox',
              style: menuContainerStyle,
              ref: handleFontMenuContainerRef,
              role: 'listbox',
            },
            maxHeight: 320,
            zIndex: 1300,
          }}
        >
          {menuChildren}
        </StyledFontFamilySelect>
      </SidebarPanelButton>
    </div>
  );
};

FontFamilyPicker.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  isSelectionActive: PropTypes.bool,
};

export default FontFamilyPicker;
